import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { HeaderLogo } from 'marketing-site/src/fin/library/components/HeaderNavigation/components/HeaderLogo'
import { mq } from 'marketing-site/src/library/utils'
import { HeaderNavigationCTA } from './components/HeaderNavigationCTA'

import { IProps } from './index'
import { Icon } from 'marketing-site/src/mvp/library/elements/Icon'

export const HeaderNavigation = ({
  contentType = 'flex',
  theme = 'light',
  isTheLogoClickable,
  cta,
  secondCta,
  isLogoOnly = false,
}: IProps): React.ReactElement => {
  const headerRef = useRef<HTMLElement>(null)

  const [headerWidth, setHeaderWidth] = useState<number | null>(null)
  const [headerHeight, setHeaderHeight] = useState<number | null>(null)
  const [headerOffsetTop, setHeaderOffsetTop] = useState<number>(0)
  const [isSticky, setIsSticky] = useState(false)
  const [intercomBannerVariant, setIntercomBannerVariant] = useState<
    'inline-top' | 'inline-bottom' | 'floating-top' | 'floating-bottom' | null
  >(null)
  const [hasCookieBanner, setHasCookieBanner] = useState(false)
  const [isMobileMenuActive, setIsMobileMenuActive] = useState(false)

  const handleMenuButtonClick = () => {
    isMobileMenuActive
      ? (document.body.style.overflowY = '')
      : (document.body.style.overflowY = 'hidden')
    setIsMobileMenuActive(!isMobileMenuActive)
  }

  useEffect(() => {
    if (headerRef.current) {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => setIsSticky(entry.intersectionRatio < 1),
        { threshold: [1] },
      )

      intersectionObserver.observe(headerRef.current)

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const headerContentBox = entry.target.querySelector<HTMLElement>(
            '.header-navigation__content',
          )
          !!headerContentBox && setHeaderWidth(headerContentBox.offsetWidth)
          !!headerContentBox && setHeaderHeight(headerContentBox.offsetHeight)
        }
        setIsMobileMenuActive(false)
      })
      resizeObserver.observe(headerRef.current)

      const mutationObserver = new MutationObserver((mutations, observer) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((addedNode) => {
            if ((addedNode as HTMLIFrameElement).name === 'intercom-banner-frame') {
              if (window.location.href.includes('roofline-qa-inline-bottom')) {
                setIntercomBannerVariant('inline-bottom')
              } else if (window.location.href.includes('roofline-qa-floating-bottom')) {
                setIntercomBannerVariant('floating-bottom')
              } else if (window.location.href.includes('roofline-qa-inline')) {
                setIntercomBannerVariant('inline-top')
              } else if (window.location.href.includes('roofline-qa-floating')) {
                setIntercomBannerVariant('floating-top')
              }
            } else if ((addedNode as HTMLDivElement).id === 'intercom-cookie-consent-banner-wrap') {
              setHasCookieBanner(true)
            }
          })
          mutation.removedNodes.forEach((removedNode) => {
            if ((removedNode as HTMLIFrameElement).name === 'intercom-banner-frame') {
              setIntercomBannerVariant(null)
              observer.disconnect()
            } else if (
              (removedNode as HTMLDivElement).id === 'intercom-cookie-consent-banner-wrap'
            ) {
              setHasCookieBanner(false)
              observer.disconnect()
            }
          })
        }
      })
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }
  }, [])

  useEffect(() => {
    const intercomBannerBox = document.querySelector<HTMLIFrameElement>(
      'iframe[name="intercom-banner-frame"]',
    )
    const cookiesBannerBox = document.querySelector<HTMLDivElement>(
      '#intercom-cookie-consent-banner-wrap > div',
    )
    let intercomBannerBoxHeight = 0
    if (document.body.clientWidth < 355) {
      intercomBannerBoxHeight = 122
    } else if (document.body.clientWidth < 600) {
      intercomBannerBoxHeight = 102
    } else if (document.body.clientWidth < 694) {
      intercomBannerBoxHeight = 82
    } else {
      intercomBannerBoxHeight = 50
    }

    switch (intercomBannerVariant) {
      case 'inline-top':
        intercomBannerBox && setHeaderOffsetTop(intercomBannerBox ? intercomBannerBoxHeight : 0)
        break
      case 'floating-top':
        if (headerRef.current && intercomBannerBox) {
          intercomBannerBox.style.setProperty('animation', 'unset')
          intercomBannerBox.style.setProperty('transition', 'top 0.6s')
          intercomBannerBox.style.setProperty(
            'top',
            `${(headerHeight || 0) + (headerRef.current?.getBoundingClientRect().top || 0)}px`,
          )
        }
        break
      case 'inline-bottom':
        if (cookiesBannerBox) {
          cookiesBannerBox.style.setProperty('transition', 'bottom 0.4s')
          cookiesBannerBox.style.setProperty('bottom', `${intercomBannerBoxHeight || 0}px`)
        }
        break
      case 'floating-bottom':
        if (intercomBannerBox) {
          intercomBannerBox.style.setProperty('animation', 'unset')
          intercomBannerBox.style.setProperty('transition', 'bottom 0.6s')
          intercomBannerBox.style.setProperty(
            'bottom',
            `${(cookiesBannerBox?.offsetHeight || 0) + 16}px`,
          )
        }
        break
      case null:
      default:
        setHeaderOffsetTop(0)
        cookiesBannerBox && cookiesBannerBox.style.setProperty('bottom', '0')
        break
    }
  }, [headerWidth, headerHeight, intercomBannerVariant, hasCookieBanner])

  const dynamicTheme = theme === 'dark' && isSticky ? 'light' : theme

  const classes = classNames('header-navigation', {
    [`header-navigation--theme-${dynamicTheme}`]: theme,
    'grid-layout': contentType === 'grid',
  })

  const mobileClasses = classNames('header-navigation-mobile', {
    'header-navigation-mobile--on': isMobileMenuActive,
    'header-navigation-mobile--off': !isMobileMenuActive,
    [`header-navigation-mobile--theme-${theme}`]: theme,
  })

  return (
    <>
      <header className={classes} id="site-header" data-testid="header-navigation" ref={headerRef}>
        <nav className="header-navigation__inner">
          <div className="header-navigation__content">
            <HeaderLogo
              contentType={contentType}
              theme={dynamicTheme}
              isClickable={isTheLogoClickable}
              isoOnly={isLogoOnly}
            />
            <div className="header-navigation__ctas">
              {cta && <HeaderNavigationCTA cta={cta} />}
              <div
                className="header-navigation__second-cta-wrapper"
                data-testid="second-cta-wrapper"
              >
                {secondCta && <HeaderNavigationCTA cta={secondCta} />}
              </div>
            </div>
            <button
              type="button"
              className="menu-button"
              aria-label={isMobileMenuActive ? 'Close navigation menu' : 'Open navigation menu'}
              aria-pressed={isMobileMenuActive}
              aria-expanded={isMobileMenuActive}
              aria-haspopup="true"
              data-testid="menu-button"
              onClick={handleMenuButtonClick}
            >
              <Icon icon="hamburger-menu" size="large" />
            </button>
          </div>
        </nav>
        {isMobileMenuActive && (
          <div className={mobileClasses}>
            <div className="header-navigation-mobile__ctas">
              {cta && <HeaderNavigationCTA cta={cta} overrideProps={{ small: false }} />}
              {secondCta && (
                <HeaderNavigationCTA cta={secondCta} overrideProps={{ small: false }} />
              )}
              <div
                className="header-navigation__backdrop"
                onClick={handleMenuButtonClick}
                onKeyDown={handleMenuButtonClick}
                role="button"
                tabIndex={0}
              />
            </div>
          </div>
        )}
      </header>

      <style jsx>{`
        .header-navigation {
          position: sticky;
          // The following ”- 1” in the top property and the ”padding-top: 1px”
          // is necessary for the intersection observer to work as expected.
          top: ${headerOffsetTop - 1}px;
          padding-top: 1px;
          width: 100%;
          max-height: 100vh;
          z-index: 100; /* stay above page content z-indices */
          padding-left: 1.25rem;
          padding-right: 1.25rem;
          transition: top 0.24s ease-out, padding 0.24s, background-color 0.32s;

          &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            transition: background-color 0.32s;
          }

          &__inner {
            max-width: ${contentType === 'flex' ? '1290px' : '77.5rem'};
            margin: 0 auto;
            position: relative;
          }

          &__content {
            grid-column: 1/-1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
          }

          &__backdrop {
            background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
            opacity: 25%;
            z-index: -1;
          }

          &__ctas {
            display: none;
          }

          &--theme-light {
            --text-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
            background-color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};

            &::before {
              background-color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};
            }

            .menu-button {
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              &:focus-visible {
                outline: 1px dotted ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
              }
            }
          }

          &--theme-dark {
            --text-color: var(--paper);
            background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

            &::before {
              background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
            }

            .menu-button {
              color: var(--paper);

              &:focus-visible {
                outline: 1px dotted var(--paper);
              }
            }

            :global(.header-navigation-section__title) {
              color: var(--paper);
            }
          }

          &--theme-resolution-bot {
            --text-color: var(--paper);
            background-color: black;
            position: unset;

            &::before {
              background-color: black;
            }

            .menu-button {
              color: var(--paper);

              &:focus-visible {
                outline: 1px dotted var(--paper);
              }
            }

            :global(.header-navigation-section__title) {
              color: var(--paper);
            }
          }
        }

        .menu-button {
          margin-left: auto;
          position: relative;
          cursor: pointer;

          &:focus-visible {
            outline-offset: 3px;
          }

          &[aria-pressed='true'] {
            :global(.icon path:first-child) {
              transform: translate3d(0, -21%, 0) rotate(45deg);
            }

            :global(.icon path:nth-child(2)) {
              transform: translate3d(0, 21%, 0) rotate(-45deg);
            }
          }

          :global(.icon) {
            vertical-align: middle;

            :global(path) {
              transform-origin: center left;
              transition: transform 0.24s;
            }
          }
        }

        @media (${mq.tablet}) {
          .header-navigation {
            padding-left: 2rem;
            padding-right: 2rem;

            &__backdrop {
              width: 100vw;
              height: 100vh;
              position: absolute;
              top: ${headerHeight}px;
              left: 0;
              bottom: 0;
              right: 0;
              z-index: -1;
            }

            .menu-button {
              display: none;
            }

            &__ctas {
              display: flex;
              margin-left: auto;
              gap: 0.75rem;
            }
          }
        }

        @media (${mq.desktop}) {
          .header-navigation {
            padding-left: 2.5rem;
            padding-right: 2.5rem;
          }
        }

        @media (${mq.desktopLg}) {
          .header-navigation {
            &__content {
              padding-left: 0;
              padding-right: 0;
            }
          }
        }

        .header-navigation-mobile {
          width: 100%;
          height: calc(100vh - 89px);
          display: flex;
          flex-direction: column;
          position: absolute;
          right: 0;
          transition: background-color 0.32s;

          &--on {
            visibility: visible;
            background-color: $white;
            opacity: 1;
            z-index: 1;
          }

          &--off {
            visibility: hidden;
            opacity: 0;
          }

          &__items {
            display: flex;
            flex-direction: column;
            width: 100%;
            overflow-y: auto;
          }

          &__ctas {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 1.5rem 1.25rem;

            :global(.header-navigation-cta .cta-link) {
              width: 100%;
            }
          }
        }
      `}</style>

      <style global jsx>{`
        @keyframes banner-float-in-down {
          0% {
            top: -48px;
          }
          100% {
            top: ${(headerHeight || 0) + (headerRef.current?.getBoundingClientRect().top || 0)}px;
          }
        }

        @keyframes banner-float-in-up {
          0% {
            bottom: -48px;
          }
          100% {
            bottom: ${(headerHeight || 0) +
            (headerRef.current?.getBoundingClientRect().top || 0)}px;
          }
        }
      `}</style>
    </>
  )
}
